/* eslint-disable camelcase, @typescript-eslint/no-use-before-define */
import { type Properties } from 'posthog-js';
import { useEffect } from 'react';

import { usePostHog } from './usePostHog';

type Whoami = {
  email?: string;
  first_name?: string;
  last_name?: string;
  external_id?: string;
  wx_external_id?: string | null;
  wx_audience_external_id?: string | null;
  account_id?: string;
  account_wx_external_id?: string;
  last_sign_in_at?: string;
};

const whoamiToIdentifyPropertyKeys: Record<keyof Whoami, string> = {
  email: 'email',
  first_name: 'firstname',
  last_name: 'lastname',
  external_id: 'hire_external_id',
  account_id: 'account_id',
  last_sign_in_at: 'last_login_hire',
  account_wx_external_id: 'company_uuid',
  wx_external_id: 'user_uuid',
  wx_audience_external_id: 'audience_uuid',
};

export function usePostHogIdentify(whoami: Whoami | undefined): void {
  const posthog = usePostHog();
  useEffect(() => {
    /* eslint-disable-next-line no-underscore-dangle */
    if (!whoami?.external_id || posthog._isIdentified()) {
      return;
    }

    posthog.identify(whoami.external_id, identifyPropertiesFromWhoami(whoami));

    if (whoami.account_id) {
      posthog.group('account', whoami.account_id);
    }
  }, [posthog, whoami]);
}

function identifyPropertiesFromWhoami(whoami: Whoami): Properties {
  const properties: Properties = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  Object.entries(whoamiToIdentifyPropertyKeys).forEach(
    ([whoamiKey, propertyKey]) => {
      if (typeof whoami[whoamiKey as keyof Whoami] === 'string') {
        properties[propertyKey] = whoami[whoamiKey as keyof Whoami];
      }
    },
  );

  return properties;
}
